/**
 * Shared, reference-counted Google Photorealistic 3D Tiles context (#179) —
 * the terrainContext (#228) idiom applied to the on-demand photoreal tileset.
 *
 * The flythrough streams Google Photorealistic 3D Tiles ONLY while a flythrough
 * is running: the first acquire (0 → 1) adds the caller-created tileset to the
 * scene; the last release (1 → 0) removes it (Cesium's primitives.remove
 * destroys the primitive), so nothing persistent joins the COP (design-council
 * PART IX) and the streaming cost is bounded to the flythrough.
 *
 * Mirrors terrainContext deliberately:
 *   - callers CREATE the tileset themselves (an async, token-gated call that
 *     can fail) and pass it in — this module stays pure + unit-testable;
 *   - state is keyed per viewer via a WeakMap (no cross-viewer bleed);
 *   - acquire/release pairing is the caller's responsibility (one hold flag
 *     per caller).
 *
 * If a second holder acquires while a tileset is already installed, the
 * INSTALLED tileset wins and is returned — the caller must destroy its own
 * redundant tileset if the returned one differs from what it passed.
 */

/** The slice of Cesium3DTileset this context needs — structural for tests. */
export interface PhotorealTileset {
  destroy(): void;
  isDestroyed(): boolean;
}

/** The slice of Cesium.Viewer this context needs — structural for tests. */
export interface PhotorealViewer {
  isDestroyed?(): boolean;
  scene: {
    primitives: {
      add(primitive: unknown): unknown;
      /** Cesium removes AND destroys by default (destroyPrimitives = true). */
      remove(primitive: unknown): boolean;
    };
  };
}

interface TilesRefState {
  count: number;
  tileset: PhotorealTileset;
}

const STATE = new WeakMap<PhotorealViewer, TilesRefState>();

/**
 * Acquire the photoreal tileset on `viewer`. On the first (0 → 1) acquire the
 * passed `tileset` is added to the scene and becomes the installed tileset;
 * later acquires only bump the count. Returns the INSTALLED tileset — if it is
 * not the one you passed, destroy yours.
 */
export function acquirePhotorealTiles(
  viewer: PhotorealViewer,
  tileset: PhotorealTileset,
): PhotorealTileset {
  if (viewer.isDestroyed?.()) return tileset;
  const existing = STATE.get(viewer);
  if (existing) {
    existing.count += 1;
    return existing.tileset;
  }
  STATE.set(viewer, { count: 1, tileset });
  viewer.scene.primitives.add(tileset);
  return tileset;
}

/**
 * Release one photoreal-tiles reference on `viewer`. Only the LAST release
 * (1 → 0) removes the tileset from the scene (which destroys it). A release
 * with no outstanding acquire is a safe no-op.
 */
export function releasePhotorealTiles(viewer: PhotorealViewer): void {
  const state = STATE.get(viewer);
  if (!state) return;
  state.count -= 1;
  if (state.count > 0) return;
  STATE.delete(viewer);
  if (viewer.isDestroyed?.()) return;
  const removed = viewer.scene.primitives.remove(state.tileset);
  // Belt-and-braces: if the scene no longer held it, destroy directly so the
  // tile cache + workers are actually freed.
  if (!removed && !state.tileset.isDestroyed()) state.tileset.destroy();
}

/** Test/inspection helper: current photoreal-tiles reference count. */
export function photorealTilesRefCount(viewer: PhotorealViewer): number {
  return STATE.get(viewer)?.count ?? 0;
}
